"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Line, LineChart, Bar, BarChart } from "recharts";
import { Shield, Network, Cpu, Zap, Terminal, Activity } from "lucide-react";
const cpuData = [{
  time: "0s",
  usage: 10,
  standard: 45
}, {
  time: "1s",
  usage: 25,
  standard: 52
}, {
  time: "2s",
  usage: 45,
  standard: 48
}, {
  time: "3s",
  usage: 70,
  standard: 60
}, {
  time: "4s",
  usage: 85,
  standard: 55
}, {
  time: "5s",
  usage: 95,
  standard: 49
}, {
  time: "6s",
  usage: 100,
  standard: 51
}];
const cpuConfig = {
  usage: {
    label: "Stability Index (%)",
    color: "var(--chart-2)"
  },
  standard: {
    label: "Baseline (%)",
    color: "var(--muted-foreground)"
  }
};
const latencyData = [{
  packet: "1",
  latency: 95,
  standard: 40
}, {
  packet: "2",
  latency: 92,
  standard: 35
}, {
  packet: "3",
  latency: 98,
  standard: 45
}, {
  packet: "4",
  latency: 94,
  standard: 38
}, {
  packet: "5",
  latency: 97,
  standard: 42
}, {
  packet: "6",
  latency: 99,
  standard: 39
}];
const latencyConfig = {
  latency: {
    label: "Dignity Score",
    color: "var(--chart-1)"
  },
  standard: {
    label: "Average Outcome",
    color: "var(--muted-foreground)"
  }
};
const memoryData = [{
  cycle: "Truth",
  footprint: 85
}, {
  cycle: "Resp.",
  footprint: 92
}, {
  cycle: "Und.",
  footprint: 78
}, {
  cycle: "Skills",
  footprint: 95
}, {
  cycle: "Tho.",
  footprint: 88
}];
const memoryConfig = {
  footprint: {
    label: "Impact Growth (%)",
    color: "var(--chart-3)"
  }
};
const features = [{
  title: "Truth & Transparency",
  tagline: "The Foundation of Stability.",
  description: "We believe that honest assessment and radical transparency are the first steps toward healing. Our case management approach prioritizes the truth of each individual's journey, creating a clear roadmap for reintegration and long-term success.",
  icon: Shield,
  chartType: "cpu",
  accentColor: "text-secondary",
  accentBg: "bg-secondary/10",
  accentBorder: "border-secondary/20"
}, {
  title: "Responsibility & Agency",
  tagline: "Empowering Personal Leadership.",
  description: "Restoring dignity means restoring the power of choice. Through our mentorship and life skills development programs, we help individuals reclaim responsibility for their futures, fostering the independence needed for sustainable living.",
  icon: Network,
  chartType: "latency",
  accentColor: "text-primary",
  accentBg: "bg-primary/10",
  accentBorder: "border-primary/20"
}, {
  title: "Understanding & Growth",
  tagline: "Compassion Meets Action.",
  description: "Our community resource navigation is built on deep understanding of the systemic barriers our participants face. We provide the skills and thoughtful support necessary to navigate complex pathways toward a second chance.",
  icon: Cpu,
  chartType: "memory",
  accentColor: "text-chart-3",
  accentBg: "bg-chart-3/10",
  accentBorder: "border-chart-3/20"
}, {
  title: "Skills",
  tagline: "Empowering Capabilities.",
  description: "We equip participants with practical skills and tools to succeed in daily life and employment.",
  icon: Zap,
  chartType: "cpu",
  accentColor: "text-primary",
  accentBg: "bg-primary/10",
  accentBorder: "border-primary/20"
}, {
  title: "Thoughtfulness",
  tagline: "Compassionate Engagement.",
  description: "Our approach emphasizes thoughtful, individualized support that respects each person's unique journey.",
  icon: Activity,
  chartType: "latency",
  accentColor: "text-secondary",
  accentBg: "bg-secondary/10",
  accentBorder: "border-secondary/20"
}];
const backgroundCodeSnippet = `

const mission = "Restoring Stability";
const values = ["Truth", "Responsibility", "Understanding", "Skills", "Thoughtfulness"];

function empower(individual) {
  individual.status = "Dignified";
  individual.opportunity = "Unlimited";
  return individual.transform();
}
`;
export default function TechnicalAdvantages() {
  return <section data-section-id="222684" id="the-trust-framework" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div className="absolute top-1/4 left-[-10%] h-[500px] w-[500px] rounded-full bg-secondary/5 blur-[120px]" />
        <div className="absolute right-[-10%] bottom-1/4 h-[400px] w-[400px] rounded-full bg-primary/5 blur-[100px]" />
        
        <pre className="absolute top-10 right-10 rotate-12 font-mono text-[10px] leading-relaxed text-muted-foreground/5 md:text-xs">
          {backgroundCodeSnippet}
        </pre>
        <pre className="absolute bottom-10 left-10 -rotate-6 font-mono text-[10px] leading-relaxed text-muted-foreground/5 md:text-xs">
          {backgroundCodeSnippet}
        </pre>
        
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.05]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-20 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
            <Zap className="h-4 w-4" />
            <span className="font-default">The TRUST Methodology</span>
          </div>
          <h2 className="font-default mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            The <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">TRUST Framework</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground font-default">
            Our evidence-based approach to community restoration focuses on five core pillars that transform lives from the inside out.
          </p>
        </div>

        <div className="flex flex-col gap-24">
          {features.map((feature, i) => <div key={feature.title} data-index={i} className={`flex flex-col gap-8 md:items-center ${i % 2 !== 0 ? "md:flex-row-reverse" : "md:flex-row"}`}>
              {}
              <div className="flex-1 space-y-6">
                <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl border ${feature.accentBorder} ${feature.accentBg} shadow-sm`}>
                  <feature.icon className={`h-6 w-6 ${feature.accentColor}`} />
                </div>
                <div>
                  <h3 className="font-default mb-2 text-2xl font-bold md:text-3xl text-primary">
                    {feature.title}
                  </h3>
                  <p className={`mb-4 text-sm font-bold tracking-widest uppercase ${feature.accentColor}`}>
                    {feature.tagline}
                  </p>
                  <p className="text-muted-foreground leading-relaxed font-default">
                    {feature.description}
                  </p>
                </div>
                <div className="pt-4">
                  <Button asChild variant="outline" className="group border-border/50 bg-background/50 backdrop-blur-sm hover:bg-accent hover:text-accent-foreground rounded-full px-6">
                    <Link to="/ProgramsPage">
                      Explore Our Programs
                      <Activity className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>

              {}
              <div className="flex-1">
                <Card className={`overflow-hidden border-border/50 bg-card/60 backdrop-blur-xl rounded-3xl shadow-xl ${i % 3 === 0 ? "shadow-secondary/5" : i % 3 === 1 ? "shadow-primary/5" : "shadow-chart-3/5"}`}>
                  
                  <CardContent className="p-6">
                    {i === 0 ? <img src="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/00646266-46aa-4d03-a048-c3d62971eccb.png?v=2r97sxugvt6" data-wvc-srcset="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/00646266-46aa-4d03-a048-c3d62971eccb.png?v=ha5uoyqwx6j-wvc-srcset" data-wvc-sizes="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/00646266-46aa-4d03-a048-c3d62971eccb.png?v=cvxu1mb11n7-wvc-sizes" alt="Impact graph" className="w-full h-[250px] object-cover rounded-xl" loading="lazy" decoding="async" /> : <>
                        {feature.chartType === "cpu" && (i === 3 ? <img src="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/25b2a86a-f713-44cf-841b-3ce7c0a7c172.png?v=9jpn3nj3htc" data-wvc-srcset="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/25b2a86a-f713-44cf-841b-3ce7c0a7c172.png?v=xahyco5i3vg-wvc-srcset" data-wvc-sizes="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/25b2a86a-f713-44cf-841b-3ce7c0a7c172.png?v=vtt98vnbb3r-wvc-sizes" alt="Skills development" className="w-full h-[250px] object-cover rounded-xl" loading="lazy" decoding="async" /> : <ChartContainer config={cpuConfig} className="h-[250px] w-full">
                            <AreaChart data={cpuData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }}>
                              <defs>
                                <linearGradient id={`fillUsage-${i}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="var(--color-usage)" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="var(--color-usage)" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.4} />
                              <XAxis dataKey="time" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                              <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `${value}%`} />
                              <ChartTooltip content={<ChartTooltipContent />} />
                              <Area type="monotone" dataKey="standard" stroke="var(--color-standard)" fill="transparent" strokeWidth={2} strokeDasharray="5 5" />
                              <Area type="monotone" dataKey="usage" stroke="var(--color-usage)" fill={`url(#fillUsage-${i})`} strokeWidth={3} />
                            </AreaChart>
                          </ChartContainer>)}

                        {feature.chartType === "latency" && (i === 1 || i === 4 ? <img src="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/7e513cf6-aef9-4dc3-88d9-140682da7d5d.png?v=g3gjedjzoll" data-wvc-srcset="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/7e513cf6-aef9-4dc3-88d9-140682da7d5d.png?v=lsb5qw6cjmg-wvc-srcset" data-wvc-sizes="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/7e513cf6-aef9-4dc3-88d9-140682da7d5d.png?v=m4qvccsyz4g-wvc-sizes" alt="Impact graph" className="w-full h-[250px] object-cover rounded-xl" loading="lazy" decoding="async" /> : <ChartContainer config={latencyConfig} className="h-[250px] w-full">
                            <LineChart data={latencyData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.4} />
                              <XAxis dataKey="packet" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                              <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `${value}`} />
                              <ChartTooltip content={<ChartTooltipContent />} />
                              <Line type="monotone" dataKey="standard" stroke="var(--color-standard)" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                              <Line type="monotone" dataKey="latency" stroke="var(--color-latency)" strokeWidth={3} dot={{
                        r: 4,
                        fill: "var(--background)",
                        strokeWidth: 2
                      }} activeDot={{
                        r: 6
                      }} />
                            </LineChart>
                          </ChartContainer>)}

                        {feature.chartType === "memory" && <img src="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/5a4f6b6a-b321-4fe8-911e-b600e4eff668.png?v=8wawwei2ah3" data-wvc-srcset="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/5a4f6b6a-b321-4fe8-911e-b600e4eff668.png?v=mbuvq2h0cqp-wvc-srcset" data-wvc-sizes="https://solid-silkworm.10web.cloud/wp-content/uploads/2026/04/5a4f6b6a-b321-4fe8-911e-b600e4eff668.png?v=crdfa53o97k-wvc-sizes" alt="Memory chart image" className="w-full h-[250px] object-cover rounded-xl" loading="lazy" decoding="async" />}
                      </>}
                  </CardContent>
                </Card>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}